import React from 'react';
import { Package, FileText, CreditCard, Truck, Zap, RotateCcw, ArrowRight } from 'lucide-react';
import { processSteps } from '../data/mock';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const iconMap = {
  Package,
  FileText,
  CreditCard,
  Truck,
  Zap,
  RotateCcw
};

const ProcessSection = () => {
  const navigate = useNavigate();

  // Split steps into two rows for display
  const firstRow = processSteps.slice(0, 3);
  const secondRow = processSteps.slice(3, 6);

  return (
    <section id="process" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#9aae00] font-semibold text-sm uppercase tracking-wider mb-4">
            Processus simple
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Comment ça marche ?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            De la demande à l'installation, tout est pensé pour vous simplifier la vie.
          </p>
        </div>

        {/* Process Steps - Desktop Layout */}
        <div className="hidden lg:block">
          {/* First Row */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {firstRow.map((step, index) => {
              const Icon = iconMap[step.icon];
              return (
                <React.Fragment key={step.id}>
                  {/* Step Card */}
                  <div className="relative group flex-1 max-w-sm">
                    <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-[#DFFF00]/30 h-full">
                      <div className="absolute -top-4 -left-4 w-10 h-10 bg-[#DFFF00] text-black rounded-full flex items-center justify-center font-bold text-lg shadow-lg shadow-[#DFFF00]/30 z-10">
                        {step.id}
                      </div>
                      <div className="w-16 h-16 bg-[#DFFF00]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#DFFF00]/20 transition-colors">
                        <Icon className="w-8 h-8 text-[#9aae00]" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                      <p className="text-slate-600">{step.description}</p>
                    </div>
                  </div>
                  
                  {/* Arrow Connector */}
                  {index < firstRow.length - 1 && (
                    <div className="flex-shrink-0 flex items-center px-2">
                      <div className="relative">
                        <div className="w-16 h-1 bg-[#DFFF00] rounded-full shadow-[0_0_15px_rgba(223,255,0,0.8)]" />
                        <ArrowRight className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 text-[#DFFF00] drop-shadow-[0_0_10px_rgba(223,255,0,1)]" />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Space between rows */}
          <div className="h-8"></div>

          {/* Second Row */}
          <div className="flex items-center justify-center gap-4">
            {secondRow.map((step, index) => {
              const Icon = iconMap[step.icon];
              return (
                <React.Fragment key={step.id}>
                  {/* Step Card */}
                  <div className="relative group flex-1 max-w-sm">
                    <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-[#DFFF00]/30 h-full">
                      <div className="absolute -top-4 -left-4 w-10 h-10 bg-[#DFFF00] text-black rounded-full flex items-center justify-center font-bold text-lg shadow-lg shadow-[#DFFF00]/30 z-10">
                        {step.id}
                      </div>
                      <div className="w-16 h-16 bg-[#DFFF00]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#DFFF00]/20 transition-colors">
                        <Icon className="w-8 h-8 text-[#9aae00]" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                      <p className="text-slate-600">{step.description}</p>
                    </div>
                  </div>
                  
                  {/* Arrow Connector */}
                  {index < secondRow.length - 1 && (
                    <div className="flex-shrink-0 flex items-center px-2">
                      <div className="relative">
                        <div className="w-16 h-1 bg-[#DFFF00] rounded-full shadow-[0_0_15px_rgba(223,255,0,0.8)]" />
                        <ArrowRight className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 text-[#DFFF00] drop-shadow-[0_0_10px_rgba(223,255,0,1)]" />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Process Steps - Tablet Layout (2 columns) */}
        <div className="hidden md:grid lg:hidden grid-cols-2 gap-6">
          {processSteps.map((step, index) => {
            const Icon = iconMap[step.icon];
            const isRightColumn = index % 2 === 1;
            const hasNextInRow = index % 2 === 0 && index < processSteps.length - 1;
            const hasNextRow = index % 2 === 1 && index < processSteps.length - 1;
            
            return (
              <div key={step.id} className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-[#DFFF00]/30">
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-[#DFFF00] text-black rounded-full flex items-center justify-center font-bold text-sm shadow-lg shadow-[#DFFF00]/30 z-10">
                    {step.id}
                  </div>
                  <div className="w-12 h-12 bg-[#DFFF00]/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#9aae00]" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
                
                {/* Horizontal connector to next card in row */}
                {hasNextInRow && (
                  <div className="absolute top-1/2 -right-3 transform -translate-y-1/2 z-20">
                    <div className="flex items-center">
                      <div className="w-6 h-1 bg-[#DFFF00] rounded-full shadow-[0_0_10px_rgba(223,255,0,0.8)]" />
                      <ArrowRight className="w-4 h-4 text-[#DFFF00] -ml-1" />
                    </div>
                  </div>
                )}
                
                {/* Vertical connector to next row */}
                {hasNextRow && (
                  <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 z-20">
                    <div className="w-1 h-6 bg-[#DFFF00] rounded-full shadow-[0_0_10px_rgba(223,255,0,0.8)]" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Process Steps - Mobile Layout (1 column) */}
        <div className="md:hidden space-y-4">
          {processSteps.map((step, index) => {
            const Icon = iconMap[step.icon];
            const hasNext = index < processSteps.length - 1;
            
            return (
              <div key={step.id} className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-[#DFFF00] text-black rounded-full flex items-center justify-center font-bold text-sm shadow-lg shadow-[#DFFF00]/30 z-10">
                    {step.id}
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#DFFF00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#9aae00]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">{step.title}</h3>
                      <p className="text-slate-600 text-sm">{step.description}</p>
                    </div>
                  </div>
                </div>
                
                {/* Vertical connector */}
                {hasNext && (
                  <div className="flex justify-center py-2">
                    <div className="w-1 h-6 bg-[#DFFF00] rounded-full shadow-[0_0_10px_rgba(223,255,0,0.8)]" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary Box */}
        <div className="mt-16 bg-black rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                En résumé
              </h3>
              <p className="text-slate-300 text-lg">
                "Je prends mes écrans pour la Coupe du Monde ou la saison d'été, 
                je les utilise autant que je veux, puis je les rends."
              </p>
              <Button
                onClick={() => navigate('/reservation')}
                className="mt-6 bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-6 py-3 rounded-full"
              >
                Commencer ma réservation
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-4 justify-center md:justify-end">
              <div className="bg-white/10 backdrop-blur rounded-xl px-6 py-4 text-center">
                <p className="text-3xl font-bold text-[#DFFF00]">1 mois</p>
                <p className="text-sm text-slate-300">Minimum</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl px-6 py-4 text-center">
                <p className="text-3xl font-bold text-[#DFFF00]">24-48h</p>
                <p className="text-sm text-slate-300">Validation</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl px-6 py-4 text-center">
                <p className="text-3xl font-bold text-[#DFFF00]">100%</p>
                <p className="text-sm text-slate-300">Caution restituée*</p>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-500 italic text-center md:text-right">
          *Restituable entièrement si aucun dommage n'est constaté après contrôle du matériel.
        </p>
      </div>
    </section>
  );
};

export default ProcessSection;
